import { useState } from "react";
import { Calendar, Clock, ChevronRight } from "lucide-react";
import { jumpToExecPhase } from "@/lib/phaseNav";

// 1 working day = 8 h (consistent with /lib/hours.js)
// 1 week = 5 working days here (used to express milestones in days)
const DAYS_PER_WEEK = 5;

// Color palette for phases — alternates cyan / orange so consecutive bars are
// visually distinguishable, matching the rest of the app's neon aesthetic.
const PHASE_COLORS = [
  { bar: "#00F0FF", glow: "rgba(0,240,255,0.35)", text: "#00F0FF" },
  { bar: "#FFB800", glow: "rgba(255,184,0,0.35)", text: "#FFB800" },
  { bar: "#7B61FF", glow: "rgba(123,97,255,0.35)", text: "#A89AFF" },
  { bar: "#FF6B9D", glow: "rgba(255,107,157,0.35)", text: "#FF8FB5" },
  { bar: "#00E5A0", glow: "rgba(0,229,160,0.35)", text: "#33EDB5" },
];

const fmtDate = (d) => d.toLocaleDateString("de-DE", { day: "2-digit", month: "short" });

export default function GanttTimeline({ milestones = [], startDate, totalHoursMin, totalHoursMax }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  if (!milestones || milestones.length === 0) return null;

  const start = startDate ? new Date(startDate) : new Date();
  const totalWeeks = milestones.reduce((s, m) => s + (m.weeks || 0), 0);
  const totalDays = totalWeeks * DAYS_PER_WEEK;

  // Compute each milestone segment with cumulative offset
  let cumWeek = 0;
  const segments = milestones.map((m, i) => {
    const w = m.weeks || 0;
    const startWeek = cumWeek;
    const endWeek = cumWeek + w;
    cumWeek = endWeek;
    const startDayOffset = startWeek * DAYS_PER_WEEK;
    const endDayOffset = endWeek * DAYS_PER_WEEK;
    const startCal = new Date(start);
    startCal.setDate(startCal.getDate() + Math.round(startDayOffset * 1.4)); // 5 work days = 7 cal days
    const endCal = new Date(start);
    endCal.setDate(endCal.getDate() + Math.round(endDayOffset * 1.4));
    return {
      ...m,
      startWeek, endWeek,
      startDay: startDayOffset, endDay: endDayOffset,
      durationDays: w * DAYS_PER_WEEK,
      startCal, endCal,
      colorIdx: i % PHASE_COLORS.length,
      widthPct: totalWeeks ? (w / totalWeeks) * 100 : 0,
      offsetPct: totalWeeks ? (startWeek / totalWeeks) * 100 : 0,
    };
  });

  // Build week ruler ticks (max ~12 ticks for clarity)
  const tickStep = totalWeeks <= 12 ? 1 : Math.ceil(totalWeeks / 12);
  const ticks = [];
  for (let w = 0; w <= totalWeeks; w += tickStep) {
    ticks.push(w);
  }
  if (ticks[ticks.length - 1] !== totalWeeks) ticks.push(totalWeeks);

  // Today / kickoff calendar end date
  const projectEnd = new Date(start);
  projectEnd.setDate(projectEnd.getDate() + Math.round(totalDays * 1.4));

  return (
    <div data-testid="gantt-timeline" className="glass-surface rounded-lg p-5 sm:p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#00F0FF]/10">
            <Calendar className="w-4 h-4 text-[#00F0FF]" />
          </div>
          <div>
            <h3 className="font-['Outfit'] text-base font-semibold text-white">Projekt-Zeitstrahl</h3>
            <p className="text-[11px] text-[#606070] font-['JetBrains_Mono'] mt-0.5">
              Gantt-Visualisierung der Meilensteine
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 text-right">
          <div>
            <div className="text-[10px] text-[#606070] font-['JetBrains_Mono'] uppercase tracking-wider">Gesamt</div>
            <div className="text-sm font-['JetBrains_Mono'] font-bold text-white">
              {totalWeeks} W. <span className="text-[#A0A0B0]">≈ {totalDays} T.</span>
            </div>
          </div>
          <div>
            <div className="text-[10px] text-[#606070] font-['JetBrains_Mono'] uppercase tracking-wider">Lieferung</div>
            <div className="text-sm font-['JetBrains_Mono'] font-bold text-[#00F0FF]">
              {fmtDate(projectEnd)}
            </div>
          </div>
        </div>
      </div>

      {/* Total hours summary (if provided) */}
      {totalHoursMin != null && totalHoursMax != null && (
        <div className="mb-4 flex items-center gap-2 text-[11px] text-[#A0A0B0] font-['JetBrains_Mono']">
          <Clock className="w-3 h-3 text-[#00F0FF]" />
          {totalHoursMin}-{totalHoursMax} h Aufwand
          <ChevronRight className="w-3 h-3 text-[#606070]" />
          {Math.round(totalHoursMin / 8)}-{Math.round(totalHoursMax / 8)} Personentage à 8 h
        </div>
      )}

      {/* Week ruler */}
      <div className="relative pl-[180px] pr-2 mb-1.5">
        <div className="relative h-5 border-b border-white/10">
          {ticks.map((w) => (
            <div
              key={w}
              className="absolute top-0 h-full"
              style={{ left: `${(w / totalWeeks) * 100}%`, transform: "translateX(-50%)" }}
            >
              <span className="absolute top-0 text-[9px] font-['JetBrains_Mono'] text-[#606070] whitespace-nowrap">
                {w === 0 ? "Start" : `Wo. ${w}`}
              </span>
              <div className="absolute bottom-0 left-1/2 w-px h-1.5 bg-white/15" />
            </div>
          ))}
        </div>
      </div>

      {/* Bars */}
      <div className="space-y-2">
        {segments.map((seg, i) => {
          const c = PHASE_COLORS[seg.colorIdx];
          const isHover = hoverIdx === i;
          const isAudit = !!(seg.isAuditPhase || seg.isCriticalPath);
          // Audit/critical-path overrides the regular phase color
          const barColor = isAudit ? "#EF4444" : c.bar;
          const glowColor = isAudit ? "rgba(239,68,68,0.45)" : c.glow;
          const textColor = isAudit ? "#FCA5A5" : c.text;
          return (
            <div
              key={i}
              id={`gantt-phase-${seg.phase || i + 1}`}
              data-testid={`gantt-row-${i}`}
              className="flex items-center group p-1 -mx-1 rounded-md transition-colors cursor-pointer hover:bg-white/[0.03]"
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
              onClick={() => jumpToExecPhase(seg.phase || i + 1)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  jumpToExecPhase(seg.phase || i + 1);
                }
              }}
              role="button"
              tabIndex={0}
              title="Springe zur Executive-Summary-Phase"
            >
              {/* Label */}
              <div className="w-[180px] pr-3 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span
                    className="w-5 h-5 flex items-center justify-center rounded text-[10px] font-['JetBrains_Mono'] font-bold flex-shrink-0"
                    style={{ background: `${barColor}22`, color: textColor }}
                  >
                    {isAudit ? "⚠" : seg.phase || i + 1}
                  </span>
                  <span
                    className={`text-xs truncate ${isAudit ? "text-[#FCA5A5] font-semibold" : "text-white"}`}
                    title={seg.name}
                  >
                    {seg.name}
                  </span>
                </div>
                <div className="text-[10px] text-[#606070] font-['JetBrains_Mono'] mt-0.5 ml-7">
                  {isAudit ? (
                    <span className="text-[#EF4444] font-bold">KRIT. PFAD · </span>
                  ) : null}
                  {seg.weeks} Wo. · {seg.durationDays} T.
                </div>
              </div>

              {/* Track */}
              <div className="relative flex-1 h-8 rounded bg-white/[0.02] border border-white/5 overflow-hidden">
                {/* Subtle gridlines */}
                {ticks.slice(1, -1).map((w) => (
                  <div
                    key={w}
                    className="absolute top-0 bottom-0 w-px bg-white/[0.04]"
                    style={{ left: `${(w / totalWeeks) * 100}%` }}
                  />
                ))}
                {/* Bar */}
                <div
                  className="absolute top-1 bottom-1 rounded transition-all duration-200"
                  style={{
                    left: `${seg.offsetPct}%`,
                    width: `${seg.widthPct}%`,
                    minWidth: "10px",
                    background: isAudit
                      ? `repeating-linear-gradient(45deg, ${barColor}55 0 6px, ${barColor}aa 6px 12px)`
                      : `linear-gradient(90deg, ${barColor}33 0%, ${barColor}66 100%)`,
                    border: `${isAudit ? 1.5 : 1}px solid ${barColor}`,
                    boxShadow: isHover || isAudit ? `0 0 16px ${glowColor}` : "none",
                  }}
                >
                  <div className="h-full flex items-center justify-between px-2 gap-2">
                    <span className="text-[10px] font-['JetBrains_Mono'] font-bold whitespace-nowrap" style={{ color: textColor }}>
                      Wo. {seg.startWeek + 1}–{seg.endWeek}
                    </span>
                    {seg.widthPct > 18 && (
                      <span className="text-[9px] font-['JetBrains_Mono'] text-white/70 whitespace-nowrap hidden sm:inline">
                        {fmtDate(seg.startCal)} → {fmtDate(seg.endCal)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footnote */}
      <div className="mt-4 pt-3 border-t border-white/5 text-[10px] text-[#606070] font-['JetBrains_Mono'] flex flex-wrap items-center gap-x-4 gap-y-1">
        <span>1 Wo. = 5 Arbeitstage</span>
        <span>1 T. = 8 h</span>
        <span>Start: {fmtDate(start)}</span>
        <span className="ml-auto opacity-60">Indikative Schätzung — exakte Daten werden im Vertrag fixiert.</span>
      </div>
    </div>
  );
}
